<template>
  <div class="wrapper">
    <div class="header_wrapper">
      <v-toolbar color="primary" dark>
        <v-btn @click="routerBack" icon>
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <v-toolbar-title
          style="
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
            margin: 0;
            padding: 0;
            max-width: 70%;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          "
          class="text-center"
        >
          {{ childName }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn v-if="!translatedChats[chatId]" @click="openTranslateDialog" icon>
          <v-icon>mdi-google-translate</v-icon>
        </v-btn>
        <v-btn v-else @click="returnTranslate" icon>
          <v-icon>mdi-arrow-u-left-top</v-icon>
        </v-btn>
      </v-toolbar>
    </div>

    <div v-if="fetchLoading" class="d-flex justify-center align-center" style="height: 80vh;">
      <vue-loading
        :active="fetchLoading"
        loader="bars"
        color="#EE6090"
        :size="{ width: '30px', height: '30px' }"
      ></vue-loading>
    </div>

    <div v-else-if="chat" class="contents-wrapper pt-3">
      <div class="pa-1 grey-wrapper">
        <h5>{{ t('references.detail.date') }}</h5>
      </div>
      <div class="pa-1">
        <p>{{ dayFormat(chat.date) }}</p>
      </div>
      <div class="pa-1 grey-wrapper">
        <h5>{{ t('references.detail.title') }}</h5>
      </div>
      <div class="pa-1">
        <p>{{ displayTitle }}</p>
      </div>
      <div class="pa-1 grey-wrapper">
        <h5>{{ t('references.detail.body') }}</h5>
      </div>
      <div class="pa-1">
        <p class="pre-wrap">{{ displayBody }}</p>
      </div>
      <div v-if="chat.attachmentUrls.length">
        <div class="pa-1 grey-wrapper">
          <h5>{{ t('references.detail.attachments') }}</h5>
        </div>
        <div>
          <div v-for="(au, index) in chat.attachmentUrls" :key="au.objectKey">
            <v-card
              v-if="!isPdf(au.objectKey)"
              @click="openImg(chat, index)"
              class="pointer ma-1"
              elevation="0">
              <v-img :src="au.url ?? undefined" contain></v-img>
            </v-card>
            <v-btn
              class="my-3 mx-1"
              v-else
              color="primary"
              dark
              small
              @click="openImg(chat, index)"
              :loading="imgLoading">
              {{ t('references.detail.view') }}
            </v-btn>
          </div>
        </div>
      </div>
    </div>

    <v-dialog v-model="isTranslationDialog" persistent max-width="400px">
      <translation-modal :isOpen="isTranslationDialog" :childId="childId" :selectedChat="translationChat"
        @translate="onTranslated" @close="isTranslationDialog = false" />
    </v-dialog>

    <v-dialog
      class="pt-5"
      v-model="isAttachmentUrls"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition">
      <v-card class="pdf_wrapper pa-0">
        <div class="pdf_header_wrapper">
          <v-toolbar class="pdf_header" dark color="primary">
            <v-btn icon dark @click="closeChatImg" absolute>
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <v-spacer />
            <v-toolbar-title>{{ childName }}</v-toolbar-title>
            <v-spacer />
          </v-toolbar>
        </div>
        <div class="pdf">
          <div v-for="u in showingUrls" :key="u">
            <object :data="u" type="application/pdf" width="100%" height="100%">
              <iframe 
                :src="u"
                width="100%" 
                height="100%" 
                frameborder="0"
                allowfullscreen
              >
                {{ t('references.detail.iframeNotSupported') }}
              </iframe>
              <div class="fallback-link">
                <a :href="u" target="_blank" rel="noopener">
                  {{ t('references.detail.downloadPdf') }}
                </a>
              </div>
            </object>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="isImgDialog"
      fullscreen
      transition="dialog-bottom-transition">
      <v-card width="100%" rounded="b-0" class="img_dialog pa-0">
        <v-card
          height="40px"
          class="d-flex justify-end align-center px-2 img_dialog-header"
          rounded="b-0"
          dark
          elevation="0">
          <p class="blue--text" @click="isImgDialog = false">{{ t('references.detail.close') }}</p>
        </v-card>
        <v-card
          height="calc(100vh - 40px)"
          width="100%"
          rounded="0"
          dark
          class="d-flex flex-grow-1">
          <v-img
            class="img_dialog-img"
            width="100%"
            contain
            :src="notPdfImgSrc ?? undefined"
            alt="img"></v-img>
        </v-card>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import 'dayjs/locale/en';
import { t as translate, locale as appLocale } from '@/i18n';
dayjs.locale(appLocale === 'ja' ? 'ja' : 'en');
import { formatDisplayDate } from '@/utils/dateFormat';

import { resourceModule } from '@/store/entityModules/resource';
import { chatModule } from '@/store/entityModules/chat';
import { HomeChatResponse } from 'chaild-entity/lib';

import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/css/index.css';

import TranslationModal from '@/components/modal/TranslationModal.vue';

interface DataType {
  chatId: number;
  childId: number;
  childName: string;
  chat: HomeChatResponse | null;
  fetchLoading: boolean;
  moreFetchLoading: boolean;
  isAttachmentUrls: boolean;
  imgLoading: boolean;
  isImgDialog: boolean;
  notPdfImgSrc: string;
  showingUrls: string[];
  isTranslationDialog: boolean;
  translationChat: { chatId: number; body: string } | null;
  translatedChats: Record<number, string>;
}

export default defineComponent({
  name: 'ReferenceDetail',

  components: {
    'vue-loading': Loading,
    TranslationModal,
  },

  data: (): DataType => ({
    chatId: NaN,
    childId: NaN,
    childName: '',
    chat: null,
    fetchLoading: false,
    moreFetchLoading: false,
    imgLoading: false,
    isAttachmentUrls: false,
    isImgDialog: false,
    notPdfImgSrc: '',
    showingUrls: [],
    isTranslationDialog: false,
    translationChat: null,
    translatedChats: {},
  }),

  computed: {
    displayTitle(): string {
      const translated = this.translatedChats[this.chatId];
      if (!translated) return this.chat?.title ?? '';
      const idx = translated.indexOf('\n');
      return idx === -1 ? translated : translated.slice(0, idx);
    },
    displayBody(): string {
      const translated = this.translatedChats[this.chatId];
      if (!translated) return this.chat?.body ?? '';
      const idx = translated.indexOf('\n');
      return idx === -1 ? '' : translated.slice(idx + 1);
    },
  },

  methods: {
    getTranslatedChatsKey(): string {
      return `translatedChats_${this.childId}`;
    },
    loadTranslatedChats() {
      try {
        const raw = localStorage.getItem(this.getTranslatedChatsKey());
        this.translatedChats = raw ? JSON.parse(raw) : {};
      } catch {
        this.translatedChats = {};
      }
    },
    saveTranslatedChats() {
      try {
        localStorage.setItem(this.getTranslatedChatsKey(), JSON.stringify(this.translatedChats));
      } catch (error) {
        console.error('Translated chats save error:', error);
      }
    },
    openTranslateDialog() {
      if (!this.chat) return;
      this.translationChat = {
        chatId: this.chatId,
        body: `${this.chat.title}\n${this.chat.body}`,
      };
      this.isTranslationDialog = true;
    },
    onTranslated(_chat: { chatId: number; body: string }, translatedText: string) {
      this.translatedChats[this.chatId] = translatedText;

      const MAX_TRANSLATED_CHATS = 50;
      const chatIds = Object.keys(this.translatedChats)
        .map(Number)
        .sort((a, b) => a - b);
      const overflow = chatIds.length - MAX_TRANSLATED_CHATS;
      for (let i = 0; i < overflow; i++) {
        delete this.translatedChats[chatIds[i]];
      }
      this.saveTranslatedChats();
    },
    returnTranslate() {
      delete this.translatedChats[this.chatId];
      this.saveTranslatedChats();
    },
    t(path: string, fallback = path, params: Record<string, string | number> = {}) {
      return translate(path, fallback, params);
    },
    routerBack() {
      this.$router.replace({
        name: 'References',
        query: {
          childId: String(this.childId),
          childName: this.childName,
        },
        state: { 
          scrollY: window.history.state?.scrollY, 
          moreCnt: window.history.state?.moreCnt 
        }
      });
    },

    dayFormat(date: string) {
      return formatDisplayDate(date);
    },

    async fetchChat() {
      this.fetchLoading = true;
      const response = await chatModule.fetchChat({
        chatId: this.chatId,
      });
      this.chat = response;
      this.fetchLoading = false;
    },

    insertStr(str, index, insert) {
      return str.slice(0, index) + insert + str.slice(index, str.length);
    },
    formatAmzIso(dateStr: string): string {
      let formatted = dateStr;
      formatted = this.insertStr(formatted, 4, '-');
      formatted = this.insertStr(formatted, 7, '-');
      formatted = this.insertStr(formatted, 13, ':');
      formatted = this.insertStr(formatted, 16, ':');
      return formatted;
    },
    isPresignedUrlExpired(url: string): boolean {
      try {
        const arr = url.split(/[=&]/);
        if (arr.length >= 12) {
          const XAmzDate = dayjs(this.formatAmzIso(arr[5])).locale('ja');
          const XAmzExpires = Number(arr[7]);
          const ExpirationTime = dayjs(XAmzDate).add(XAmzExpires, 'seconds');
          const Now = dayjs();
          const isExpired = Now.isAfter(ExpirationTime);
          return isExpired;
        } else {
          return true;
        }
      } catch (error) {
        return true;
      }
    },
    isPdf(objectKey: string) {
      const arr = objectKey.split('.');
      const ext = arr[arr.length - 1];
      if (ext === 'pdf') {
        return true;
      }
      return false;
    },
    async openImg(chat: HomeChatResponse, index: number) {
      let img = chat.attachmentUrls[index].url;
      const isPdf = this.isPdf(chat.attachmentUrls[index].objectKey);
      if (isPdf) {
        const isImgExpired = this.isPresignedUrlExpired(img);
        if (isImgExpired) {
          this.imgLoading = true;
          const objectKey = chat.attachmentUrls[index].objectKey;
          const imgContent = await resourceModule.postResourceDownload({
            objectKey,
          });
          img = imgContent.preSignedUrl;
          this.imgLoading = false;
        }
        const ua = navigator.userAgent;
        const isIOS =
          ua.indexOf('iPhone') >= 0 ||
          ua.indexOf('iPad') >= 0 ||
          Number(navigator.userAgent.indexOf('iPod')) >= 0;
        if (isIOS) {
          if ((window as any).webkit.messageHandlers) {
            (window as any).webkit.messageHandlers.pdf.postMessage(`${[img]}`);
          }
        } else {
          this.isAttachmentUrls = true;
          this.showingUrls = [img];
        }
      } else {
        const isImgExpired = this.isPresignedUrlExpired(
          chat.attachmentUrls[index].url,
        );
        if (isImgExpired) {
          const objectKey = chat.attachmentUrls[index].objectKey;
          const imgContent = await resourceModule.postResourceDownload({
            objectKey,
          });
          img = imgContent.preSignedUrl;
        }
        this.notPdfImgSrc = img;
        this.isImgDialog = true;
      }
    },
    closeChatImg() {
      this.isAttachmentUrls = false;
      this.showingUrls = [];
    },
  },

  async mounted() {
    if (typeof this.$route.query.childName === 'string') {
      this.childName = this.$route.query.childName;
    }
    if (
      typeof this.$route.query.childId === 'string' &&
      typeof this.$route.query.chatId === 'string'
    ) {
      this.childId = Number(this.$route.query.childId);
      this.chatId = Number(this.$route.query.chatId);
      this.loadTranslatedChats();
      this.fetchChat();
    } else {
      this.routerBack();
    }
  },
});
</script>

<style lang="scss" scoped>
@use '../sass/ress.scss';

.wrapper {
  height: 100%;
  height: calc(var(--vh, 1vh) * 100);
  max-width: 100%;
  overflow-y: scroll;
  scroll-behavior: smooth;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
    -webkit-appearance: none;
  }
  @supports (bottom: env(safe-area-inset-bottom)) {
    padding-top: env(safe-area-inset-top);
    margin-bottom: calc(env(safe-area-inset-bottom) + 56px);
  }

  .header_wrapper {
    height: 56px;
    width: 100%;
    position: fixed;
    z-index: 1;

    .outline {
      border: 1px solid rgb(var(--v-theme-primary));
    }
  }

  .contents-wrapper {
    width: 100%;
    min-height: calc(100% - 112px);
    margin: 56px 0 56px;
    overflow-y: hidden;
  }

  .grey-wrapper {
    color: #a3a4a4;
    background-color: #f5f6f6;
  }
}

.img_dialog {
  .img_dialog-header {
    margin-top: 30px;
    @supports (bottom: env(safe-area-inset-bottom)) {
      margin-top: calc(env(safe-area-inset-top) + 15px);
    }
  }
}
.pre-wrap {
  white-space: pre-wrap;
}
</style>